import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useField, useFormikContext } from 'formik';
import {
  Offer,
  openClientQuoteDocument,
  Prospect,
  ProspectActionEnum,
  QuoteInformation,
  StudySubject,
  StudyType,
  useAddActionToProspect,
  useBillingInformationsByOrga,
  useCurrentOrganizationId,
  useGenerateQuoteFromOffers,
} from '@app/api';
import { shouldApplyVat, shouldApplyVatToBillingInfo } from 'lib/billing';
import { TotalBlock } from '../../components/TotalBlock';
import { FAQItem, useBoolModalState, useToast } from '@app/core';
import { ProspectCreationModal } from '../../components/ProspectCreationModal';
import { Theme } from '@app/theme';
import { createUseStyles } from 'react-jss';

const useStyles = createUseStyles((theme: Theme) => ({
  faqTitle: {
    ...theme.fonts.h5,
    marginTop: theme.marginBase * 9,
  },
  FAQ: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase * 2,
  },
}));

interface Props {
  offers: Offer[];
}

export const TotalSection = ({ offers }: Props) => {
  const { t } = useTranslation();
  const toast = useToast();
  const classes = useStyles();

  const currentOrganizationId = useCurrentOrganizationId();
  const { data: billingInformations } = useBillingInformationsByOrga(
    currentOrganizationId,
  );
  const { mutateAsync: addActionToProspect } = useAddActionToProspect();
  const { mutateAsync: generateQuote, isLoading: isQuoteSaving } =
    useGenerateQuoteFromOffers();

  const [
    isProspectCreationModalOpened,
    openProspectCreationModal,
    closeProspectCreationModal,
  ] = useBoolModalState();
  const { values } = useFormikContext<Partial<QuoteInformation>>();
  const { billingCountry, duration, countryKeys } = values;

  const [{ value: numberOfParticipants }] = useField(
    'numberOfParticipantRequired',
  );

  const applyVat = useMemo(() => {
    if (billingInformations) {
      return shouldApplyVatToBillingInfo(billingInformations);
    }
    if (billingCountry) {
      return shouldApplyVat(billingCountry);
    }
    return false;
  }, [billingCountry, billingInformations]);

  const onProspectCreated = useCallback(
    async (prospect: Prospect) => {
      try {
        closeProspectCreationModal();
        const quote = await generateQuote({
          ...values,
          duration: values.duration || 60,
          durationDisplayed:
            values.durationDisplayed || (values.duration || 60).toString(),
          numberOfParticipantRequired: values.numberOfParticipantRequired || 5,
          studyType: values.studyType || StudyType.MODERATED,
          studySubject: values.studySubject || StudySubject.B2C,
          prospectId: prospect.id,
          offerIds: offers.map(({ id }) => id),
        });
        await addActionToProspect({
          prospectId: prospect.id,
          action: ProspectActionEnum.SAVE_QUOTE,
          studySetupInfo: {
            studySubject: values.studySubject,
            studyType: values.studyType,
            numberOfParticipantRequired: values.numberOfParticipantRequired,
            format: values.format,
            duration: values.duration,
          },
          quoteId: quote.id,
        });

        openClientQuoteDocument(quote.ref ?? quote.id);
      } catch (e) {
        toast.exception(e);
      }
    },
    [
      toast,
      closeProspectCreationModal,
      values,
      offers,
      generateQuote,
      addActionToProspect,
    ],
  );

  if (!duration) {
    return null;
  }

  return (
    <>
      <TotalBlock
        duration={duration}
        onSaveQuoteClick={openProspectCreationModal}
        isQuoteSaving={isQuoteSaving}
        offers={offers}
        countryKeys={countryKeys}
        numberOfParticipants={numberOfParticipants}
        shouldApplyVat={applyVat}
      />
      <div className={classes.FAQ}>
        <h4 className={classes.faqTitle}>
          {t('studySetup.generalSetup.faq.title')}
        </h4>
        <FAQItem
          question="studySetup.generalSetup.faq.whatNext.question"
          answer="studySetup.generalSetup.faq.whatNext.answer"
        />
        <FAQItem
          question="studySetup.generalSetup.faq.howRecruit.question"
          answer="studySetup.generalSetup.faq.howRecruit.answer"
        />
        <FAQItem
          question="studySetup.generalSetup.faq.whatIPay.question"
          answer="studySetup.generalSetup.faq.whatIPay.answer"
        />
        <FAQItem
          question="studySetup.generalSetup.faq.WhenIPay.question"
          answer="studySetup.generalSetup.faq.WhenIPay.answer"
        />
        <FAQItem
          question="studySetup.generalSetup.faq.HowIncentivePaid.question"
          answer="studySetup.generalSetup.faq.HowIncentivePaid.answer"
        />
        <FAQItem
          question="studySetup.generalSetup.faq.changeMyMind.question"
          answer="studySetup.generalSetup.faq.changeMyMind.answer"
        />
      </div>

      <ProspectCreationModal
        isOpen={isProspectCreationModalOpened}
        onRequestClose={closeProspectCreationModal}
        onProspectCreated={onProspectCreated}
        title={t('studySetup.generalSetup.cost.saveQuoteModal.title')}
        description={t(
          'studySetup.generalSetup.cost.saveQuoteModal.description',
        )}
        submitText={t('studySetup.generalSetup.cost.saveQuoteModal.submit')}
        action={ProspectActionEnum.SAVE_QUOTE}
        billingMethod={values.billingMethod}
        billingCountryCode={values.billingCountry}
      />
    </>
  );
};
